import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { MONTH_ARR } from '../constant/date';

dayjs.extend(customParseFormat);

/**
 * DOB picker bounds so age on `referenceDate` (e.g. joining/onboarding) is within
 * [minAge, maxAge]. Falls back to today when referenceDate is missing/invalid.
 * When `clampToYear` is provided, bounds are also clamped to that calendar year.
 */
export const getDobDateBounds = (
  referenceDate?: string,
  minAge = 0,
  maxAge?: number,
  clampToYear?: string,
): { minDate: string; maxDate: string } => {
  const parsed = dayjs(referenceDate);
  const base = parsed.isValid() ? parsed : dayjs();

  // No upper age cap → allow any DOB on/before the min-age bound
  let minDate =
    maxAge != null ? base.subtract(maxAge, 'year') : dayjs('1900-01-01');
  let maxDate = base.subtract(minAge, 'year');

  const yearDate = clampToYear
    ? dayjs(clampToYear, ['YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'], true)
    : null;

  if (yearDate?.isValid()) {
    const yearStart = yearDate.startOf('year');
    const yearEnd = yearDate.endOf('year');
    // Clamp to the given year while keeping age window when they overlap
    const clampedMin = yearStart.isAfter(minDate) ? yearStart : minDate;
    const clampedMax = yearEnd.isBefore(maxDate) ? yearEnd : maxDate;
    if (clampedMin.isAfter(clampedMax)) {
      // No overlap with age window — still restrict picker to that year
      minDate = yearStart;
      maxDate = yearEnd;
    } else {
      minDate = clampedMin;
      maxDate = clampedMax;
    }
  }

  return {
    minDate: minDate.toISOString(),
    maxDate: maxDate.toISOString(),
  };
};

export function formatCompleteTime(
  date: string | number | Date | undefined | null,
): string {
  //INPUT FORMAT: 'yyyy-mm-ddThh:mm:ss.sssZ'
  //OUTPUT FORMAT: 'hh:mm AM/PM'
  if (!dateIsValid(date)) {
    return '';
  }
  const dateObj = new Date(date as string | number | Date);
  let hours = dateObj.getHours();
  let minutes: string | number = dateObj.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes;
  return `${hours}:${minutes} ${ampm}`;
}

export const dateIsValid = (
  date: string | number | Date | undefined | null,
): boolean => {
  return date
    ? !isNaN(new Date(date as string | number | Date).getTime())
    : false;
};

export const formatDate = (input: string, separator = '-') => {
  if (!input || !dateIsValid(input)) {
    return '';
  }
  const dateObj = new Date(input as string | number | Date);
  const date = dateObj.getDate();
  const month = dateObj.getMonth() + 1;
  const year = dateObj.getFullYear();
  return [
    (date < 10 ? '0' : '') + date,
    (month < 10 ? '0' : '') + month,
    year,
  ].join(separator);
};

export const formatDateReverseOrder = (input: string, separator = '-') => {
  if (!input || !dateIsValid(input)) {
    return '';
  }
  const dateObj = new Date(input as string | number | Date);
  const date = dateObj.getDate();
  const month = dateObj.getMonth() + 1;
  const year = dateObj.getFullYear();
  return [
    year,
    (month < 10 ? '0' : '') + month,
    (date < 10 ? '0' : '') + date,
  ].join(separator);
};

export const formatCompleteDateandTime = (
  input: string | number | Date | undefined | null,
  dateSeparator = '-',
  timeSeparator = ',',
): string => {
  //INPUT FORMAT: 'yyyy${separator}mm-ddThh:mm:ss.sssZ'
  //OUTPUT FORMAT: 'hh:mm AM/PM, dd-MMM-yyyy'

  if (!dateIsValid(input)) {
    return '';
  }
  const dateObj = new Date(input as string | number | Date);
  const date = dateObj.getDate();
  const month = dateObj.getMonth();
  const year = dateObj.getFullYear();
  const time = formatCompleteTime(dateObj);
  return `${time}${timeSeparator} ${
    (date < 10 ? '0' : '') + date
  }${dateSeparator}${MONTH_ARR[month]}${dateSeparator}${year}`;
};

export const formatCompleteDate = (
  input: string | number | Date | undefined | null,
) => {
  //INPUT FORMAT: 'yyyy-mm-ddThh:mm:ss.sssZ'
  //OUTPUT FORMAT: 'dd-MMM-yyyy'

  if (!input && !dateIsValid(input)) {
    return '';
  }
  if (!input) return '';
  const dateObj = new Date(input);
  const date = dateObj.getDate();
  const month = dateObj.getMonth();
  const year = dateObj.getFullYear();
  return ` ${(date < 10 ? '0' : '') + date}-${MONTH_ARR[month]}-${year}`;
};

export const formatCompleteDateOrDefault = (
  value: string | number | Date | undefined | null,
  defaultValue: string | null = null,
) => (value ? formatCompleteDate(value) : defaultValue);

export const formatCompleteDateAndTimeOrDefault = (
  value: string | number | Date | undefined | null,
  defaultValue: string | null = null,
) => (value ? formatCompleteDateandTime(value) : defaultValue);

export const convertDurationToHours = (duration: string) => {
  const totalSeconds = parseInt(duration, 10);
  if (isNaN(totalSeconds)) return duration;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

export const convertMinutesToHHMM = (totalMinutes: string): string => {
  const hours = Math.floor(parseInt(totalMinutes, 10) / 60);
  const minutes = parseInt(totalMinutes, 10) % 60;

  if (hours && minutes) return `${hours}h${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};

export const setToMidnight = (dateString: string): string => {
  //OUTPUT FORMAT: 'yyyy-mm-ddT00:00:00.000Z'
  const date = new Date(dateString);
  date.setUTCHours(0, 0, 0, 0); // Set time to midnight UTC
  return date.toISOString();
};

export function getUTCDateMidnight(dateStr: string) {
  // Create a new Date object in local timezone
  const date = new Date(dateStr);

  // Create a new Date in UTC at midnight
  const utcMidnight = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(), // Only the date part
      0,
      0,
      0,
      0, // Midnight
    ),
  );

  return utcMidnight.toISOString(); // Format: 2025-05-20T00:00:00.000Z
}

export const getTimePickerDefaultValueFromString = (time: string): string => {
  //INPUT FORMAT: 'hh:mm AM/PM'
  //OUTPUT FORMAT: 'hh:mm:ss.sssZ'
  if (!time) return '';
  const [timePart, modifier] = time.split(' ');
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = timePart.split(':').map((item) => parseInt(item));

  if (modifier === 'PM' && hours < 12) {
    hours += 12;
  }
  if (modifier === 'AM' && hours === 12) {
    hours = 0;
  }

  const today = new Date();
  today.setHours(hours, minutes, 0, 0);
  return today.toISOString();
};

export function formatDateToReadable(dateString: string): string {
  const date = new Date(dateString);

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}
